import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import CustomFilterSelect from '../../shared/customFilterSelect';
import {
  createLogisticsRequest,
  downloadLogisticsEvidenceArchive,
  getLogisticsEvidenceContext,
  getLogisticsRequestContext,
  getLogisticsRequests,
  updateLogisticsRequest
} from '../../API/apiService';
import './style.scss';

const BENEFICIARY_NAME = 'Escuelas de Formación';
const EMPTY_FORM = {
  logisticsBagId: '', code: '', eventName: '', eventObjective: '', shortDescription: '', requestDetails: '', place: '',
  address: '', phone: '', municipalityId: '', beneficiaryName: BENEFICIARY_NAME,
  deliveryNumber: '', requestDate: '', requestTime: '', quantity: ''
};

const STATUS_LABELS = {
  pending: 'Pendiente', returned: 'En subsanación', accepted: 'Aceptado', cancelled: 'Cancelado'
};

function getMinimumRequestDate(businessDays) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Bogota', year: 'numeric', month: 'numeric', day: 'numeric'
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  const date = new Date(Date.UTC(
    Number(values.year), Number(values.month) - 1, Number(values.day)
  ));
  let addedDays = 0;
  while (addedDays < businessDays) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) addedDays += 1;
  }
  return date.toISOString().slice(0, 10);
}

function Requirements() {
  const [context, setContext] = useState({ municipalities: [], bags: [], currentUser: null });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingAcceptedNovelty, setEditingAcceptedNovelty] = useState(false);
  const [evidenceReadyIds, setEvidenceReadyIds] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [evidenceRequest, setEvidenceRequest] = useState(null);
  const [evidenceContext, setEvidenceContext] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceDownloading, setEvidenceDownloading] = useState(false);
  const [evidenceSelection, setEvidenceSelection] = useState({
    mediatorIds: [], schoolIds: [], groupIds: [], meetingIds: []
  });
  const formRef = useRef(null);
  const typeTitle = requestType === 'bag_request' ? 'Solicitud de bolsa' : 'Nuevo requerimiento';
  const summary = useMemo(() => ({
    total: requests.length,
    pending: requests.filter(item => item.status === 'pending').length,
    returned: requests.filter(item => item.status === 'returned').length,
    accepted: requests.filter(item => item.status === 'accepted').length,
    cancelled: requests.filter(item => item.status === 'cancelled').length
  }), [requests]);
  const evidenceSchools = useMemo(() => {
    if (!evidenceContext || evidenceSelection.mediatorIds.length === 0) return [];
    const schoolIds = new Set(evidenceContext.groups
      .filter(group => {
        const mediatorIds = (group.mediatorIds || [group.mediatorId]).map(Number);
        return mediatorIds.some(id => evidenceSelection.mediatorIds.includes(id));
      })
      .map(group => Number(group.schoolId)));
    return evidenceContext.schools.filter(school => schoolIds.has(Number(school.id)));
  }, [evidenceContext, evidenceSelection.mediatorIds]);
  const evidenceGroups = useMemo(() => {
    if (!evidenceContext || evidenceSelection.schoolIds.length === 0) return [];
    return evidenceContext.groups.filter(group =>
      evidenceSelection.schoolIds.includes(Number(group.schoolId)) &&
      (group.mediatorIds || [group.mediatorId]).map(Number)
        .some(id => evidenceSelection.mediatorIds.includes(id))
    );
  }, [evidenceContext, evidenceSelection.mediatorIds, evidenceSelection.schoolIds]);
  const evidenceSessions = useMemo(() => {
    if (!evidenceContext || evidenceSelection.groupIds.length === 0) return [];
    return evidenceContext.sessions.filter(session =>
      evidenceSelection.groupIds.includes(Number(session.groupId))
    );
  }, [evidenceContext, evidenceSelection.groupIds]);
  const evidenceSessionGroups = useMemo(() => {
    return evidenceSelection.groupIds.map(groupId => {
      const group = evidenceGroups.find(item => Number(item.id) === Number(groupId));
      const sessions = evidenceSessions.filter(session =>
        Number(session.groupId) === Number(groupId)
      );
      const school = evidenceContext?.schools?.find(item =>
        Number(item.id) === Number(group?.schoolId)
      );
      return {
        id: groupId,
        code: group?.code || `Grupo ${groupId}`,
        schoolName: school?.name || sessions[0]?.schoolName || '',
        sessions
      };
    }).filter(group => group.sessions.length > 0);
  }, [evidenceContext, evidenceGroups, evidenceSelection.groupIds, evidenceSessions]);
  const evidenceMediatorLabel = useMemo(() => {
    const selectedNames = (evidenceContext?.mediators || [])
      .filter(mediator => evidenceSelection.mediatorIds.includes(Number(mediator.id)))
      .map(mediator => mediator.name);
    if (selectedNames.length === 0) return 'Selecciona uno o varios mediadores';
    if (selectedNames.length <= 2) return selectedNames.join(', ');
    return `${selectedNames.length} mediadores seleccionados`;
  }, [evidenceContext, evidenceSelection.mediatorIds]);
  const evidenceSchoolLabel = useMemo(() => {
    const selectedSchools = evidenceSchools.filter(item =>
      evidenceSelection.schoolIds.includes(Number(item.id))
    );
    if (selectedSchools.length === 0) return 'Selecciona una o varias instituciones';
    if (selectedSchools.length === 1) {
      const school = selectedSchools[0];
      return `${school.name}${school.code ? ` · ${school.code}` : ''}`;
    }
    return `${selectedSchools.length} instituciones seleccionadas`;
  }, [evidenceSchools, evidenceSelection.schoolIds]);

  async function loadPage() {
    setLoading(true);
    try {
      const [pageContext, items] = await Promise.all([
        getLogisticsRequestContext(), getLogisticsRequests()
      ]);
      setContext(pageContext || { municipalities: [], bags: [], currentUser: null });
      setRequests(items);
    } catch (error) {
      toast('No fue posible cargar los requerimientos', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadPage(); }, []);

  useEffect(() => {
    if (!editingId || !requestType) return undefined;
    const frame = window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editingAcceptedNovelty, editingId, requestType]);

  function updateField(event) {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
  }

  function selectBag(event) {
    const logisticsBagId = event.target.value;
    const selectedBag = (context.bags || []).find(item =>
      Number(item.id) === Number(logisticsBagId)
    );
    setForm(current => ({
      ...current,
      logisticsBagId,
      code: selectedBag?.code || '',
      eventName: selectedBag?.eventName || ''
    }));
  }

  function selectType(type) {
    setEditingId(null);
    setEditingAcceptedNovelty(false);
    setRequestType(type);
    setShowTypeSelector(false);
    setForm(EMPTY_FORM);
  }

  function closeCreator() {
    setEditingId(null);
    setEditingAcceptedNovelty(false);
    setRequestType('');
    setShowTypeSelector(false);
    setForm(EMPTY_FORM);
  }

  function editRequest(item, acceptedNovelty = false) {
    setEditingId(item.id);
    setEditingAcceptedNovelty(acceptedNovelty);
    if (acceptedNovelty) {
      setEvidenceReadyIds(current => current.filter(id => id !== item.id));
    }
    setRequestType(item.requestType);
    setShowTypeSelector(false);
    setForm({
      logisticsBagId: item.logisticsBagId || '',
      code: item.code || '',
      eventName: item.eventName || '',
      eventObjective: item.eventObjective || '',
      shortDescription: item.shortDescription || '',
      requestDetails: item.requestDetails || '',
      place: item.place || '',
      address: item.address || '',
      phone: item.phone || '',
      municipalityId: item.municipalityId || '',
      beneficiaryName: item.beneficiaryName || BENEFICIARY_NAME,
      deliveryNumber: item.deliveryNumber || '',
      requestDate: item.requestDate || '',
      requestTime: item.requestTime || '',
      quantity: item.quantity || ''
    });
  }

  async function openEvidenceDownload(item) {
    setEvidenceRequest(item);
    setEvidenceContext(null);
    setEvidenceLoading(true);
    setEvidenceSelection({ mediatorIds: [], schoolIds: [], groupIds: [], meetingIds: [] });
    try {
      const downloadContext = await getLogisticsEvidenceContext(item.id);
      setEvidenceContext(downloadContext);
      const responsibleId = Number(item.responsibleId);
      const defaultMediator = downloadContext?.mediators?.some(mediator =>
        Number(mediator.id) === responsibleId
      ) ? [responsibleId] : [];
      setEvidenceSelection({
        mediatorIds: defaultMediator, schoolIds: [], groupIds: [], meetingIds: []
      });
    } catch (error) {
      toast('No fue posible cargar las opciones de evidencias', { type: 'error' });
      setEvidenceRequest(null);
    } finally {
      setEvidenceLoading(false);
    }
  }

  function closeEvidenceDownload() {
    if (evidenceDownloading) return;
    setEvidenceRequest(null);
    setEvidenceContext(null);
    setEvidenceSelection({ mediatorIds: [], schoolIds: [], groupIds: [], meetingIds: [] });
  }

  function selectEvidenceMediators(_key, mediatorIds) {
    setEvidenceSelection({
      mediatorIds: mediatorIds.map(Number),
      schoolIds: [], groupIds: [], meetingIds: []
    });
  }

  function selectEvidenceSchools(_key, schoolIds) {
    setEvidenceSelection(current => ({
      ...current, schoolIds: schoolIds.map(Number), groupIds: [], meetingIds: []
    }));
  }

  function toggleEvidenceGroup(groupId) {
    setEvidenceSelection(current => ({
      ...current,
      groupIds: current.groupIds.includes(groupId)
        ? current.groupIds.filter(id => id !== groupId)
        : [...current.groupIds, groupId],
      meetingIds: []
    }));
  }

  function toggleEvidenceSession(meetingId) {
    setEvidenceSelection(current => ({
      ...current,
      meetingIds: current.meetingIds.includes(meetingId)
        ? current.meetingIds.filter(id => id !== meetingId)
        : [...current.meetingIds, meetingId]
    }));
  }

  async function downloadEvidence(event) {
    event.preventDefault();
    if (!evidenceContext?.hasChecklist) {
      toast('No se puede descargar: Logística aún no ha cargado la lista de chequeo', {
        type: 'warning'
      });
      return;
    }
    const { mediatorIds, schoolIds, groupIds, meetingIds } = evidenceSelection;
    if (!mediatorIds.length || !schoolIds.length || !groupIds.length || !meetingIds.length) {
      toast('Selecciona mediadores, institución, grupos y sesiones', { type: 'warning' });
      return;
    }
    const missingSessions = evidenceSessions.filter(session =>
      meetingIds.includes(Number(session.id)) && !session.hasAttendanceEvidence
    );
    if (missingSessions.length > 0) {
      toast('No se puede descargar: una o más sesiones no tienen evidencia de asistencia', {
        type: 'warning'
      });
      return;
    }

    setEvidenceDownloading(true);
    try {
      const blob = await downloadLogisticsEvidenceArchive(evidenceRequest.id, {
        mediatorIds,
        schoolIds: schoolIds.filter(id => id > 0),
        groupIds: groupIds.filter(id => id > 0),
        meetingIds: meetingIds.filter(id => id > 0),
        incidenceActionIds: meetingIds.filter(id => id < 0).map(id => Math.abs(id))
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeCode = String(evidenceRequest.code || evidenceRequest.id)
        .replace(/[^a-zA-Z0-9_-]+/g, '_');
      link.href = objectUrl;
      link.download = `evidencias_logisticas_${safeCode}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast('Descarga de evidencias iniciada', { type: 'success' });
      setEvidenceRequest(null);
      setEvidenceContext(null);
      setEvidenceSelection({ mediatorIds: [], schoolIds: [], groupIds: [], meetingIds: [] });
    } catch (error) {
      let message = error?.response?.data?.message;
      if (error?.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text())?.message;
        } catch (_) {}
      }
      toast(
        message === 'LOGISTICS_CHECKLIST_MISSING'
          ? 'No se puede descargar porque Logística aún no ha cargado la lista de chequeo'
          : message === 'LOGISTICS_ATTENDANCE_EVIDENCE_MISSING'
            ? 'No se puede descargar porque falta la asistencia de una o más sesiones'
            : 'No fue posible generar las evidencias logísticas',
        { type: 'error' }
      );
    } finally {
      setEvidenceDownloading(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        requestType,
        hasNovelty: editingAcceptedNovelty,
        logisticsBagId: form.logisticsBagId ? Number(form.logisticsBagId) : null,
        municipalityId: form.municipalityId ? Number(form.municipalityId) : null,
        quantity: form.quantity ? Number(form.quantity) : null
      };
      const saved = editingId
        ? await updateLogisticsRequest(editingId, payload)
        : await createLogisticsRequest(payload);
      if (saved && editingId) {
        setRequests(current => current.map(item => item.id === saved.id ? saved : item));
      } else if (saved) {
        setRequests(current => [saved, ...current]);
      }
      else await loadPage();
      toast(
        editingId
          ? 'Requerimiento actualizado y enviado nuevamente a revisión'
          : requestType === 'bag_request'
            ? `Solicitud creada con código ${saved?.code || ''}`.trim()
            : 'Requerimiento enviado a Logística',
        { type: 'success' }
      );
      closeCreator();
    } catch (error) {
      const message = error?.response?.data?.message;
      const validationMessages = {
        PHONE_INVALID: 'El teléfono debe tener 10 dígitos y comenzar por 3.',
        REQUEST_DATE_MINIMUM_BUSINESS_DAYS: requestType === 'new_requirement'
          ? 'Los nuevos requerimientos deben solicitarse con mínimo 6 días hábiles de anticipación.'
          : 'Las solicitudes de bolsa deben realizarse con mínimo 3 días hábiles de anticipación.'
      };
      toast(
        validationMessages[message] || (editingId
          ? 'No fue posible actualizar el requerimiento. Revisa los campos.'
          : 'No fue posible crear el requerimiento. Revisa los campos.'),
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className='requirements-page'>
      <div className='requirements-page__content'>
        <header className='requirements-page__header'>
          <div>
            <span className='requirements-page__eyebrow'>GESTIÓN LOGÍSTICA</span>
            <h1>Requerimientos</h1>
            <p>Registra solicitudes y consulta su estado de revisión.</p>
          </div>
          <button type='button' className='requirements-page__create' onClick={() => {
            setRequestType('');
            setEditingId(null);
            setEditingAcceptedNovelty(false);
            setShowTypeSelector(true);
          }}>
            <span className='material-symbols-rounded'>add</span>
            Crear requerimiento
          </button>
        </header>

        <div className='requirements-page__summary'>
          <span>Total <strong>{summary.total}</strong></span>
          <span>Pendientes <strong>{summary.pending}</strong></span>
          <span>En subsanación <strong>{summary.returned}</strong></span>
          <span>Aceptados <strong>{summary.accepted}</strong></span>
          <span>Cancelados <strong>{summary.cancelled}</strong></span>
        </div>

        {showTypeSelector && (
          <section className='requirements-page__chooser' aria-labelledby='request-type-title'>
            <SectionTitle step='PASO 1' title='¿Qué deseas crear?' id='request-type-title' onClose={closeCreator} />
            <div className='requirements-page__type-grid'>
              <button type='button' onClick={() => selectType('new_requirement')}>
                <span className='material-symbols-rounded'>event_note</span>
                <strong>Nuevo requerimiento</strong>
                <small>Solicitud logística asociada a un evento o fuera de la bolsa.</small>
              </button>
              <button type='button' onClick={() => selectType('bag_request')}>
                <span className='material-symbols-rounded'>package_2</span>
                <strong>Solicitud de bolsa</strong>
                <small>Entrega de la bolsa que cada mediador tiene.</small>
              </button>
            </div>
          </section>
        )}

        {requestType && (
          <form ref={formRef} className='requirements-form' onSubmit={submit}>
            <SectionTitle step={editingId ? 'EDICIÓN' : 'PASO 2'} title={editingId ? `Editar ${typeTitle.toLowerCase()}` : typeTitle} onClose={closeCreator} />
            <div className='requirements-form__grid'>
              {requestType === 'new_requirement' && (
                <>
                  <Field label='Nombre del evento *' name='eventName' value={form.eventName} onChange={updateField} required />
                  <label>
                    <span>Nombre del beneficiario *</span>
                    <select name='beneficiaryName' value={form.beneficiaryName} onChange={updateField} required>
                      <option value={BENEFICIARY_NAME}>{BENEFICIARY_NAME}</option>
                    </select>
                  </label>
                  <Field label='Objetivo del evento *' name='eventObjective' value={form.eventObjective} onChange={updateField} required wide textarea />
                  <Field label='Breve descripción *' name='shortDescription' value={form.shortDescription} onChange={updateField} required wide textarea />
                  <Field label='Solicitud *' name='requestDetails' value={form.requestDetails} onChange={updateField} required wide textarea />
                </>
              )}

              {requestType === 'bag_request' && (
                <>
                  <label className='is-wide'>
                    <span>Bolsa asignada *</span>
                    <select
                      name='logisticsBagId'
                      value={form.logisticsBagId}
                      onChange={selectBag}
                      required>
                      <option value=''>Selecciona una bolsa</option>
                      {(context.bags || []).map(item => (
                        <option value={item.id} key={item.id}>
                          {item.code} · {item.eventName}
                        </option>
                      ))}
                    </select>
                    {(context.bags || []).length === 0 && (
                      <small>Logística aún no te ha asignado ninguna bolsa.</small>
                    )}
                  </label>
                  <Field label='Código de la solicitud' value={form.code} readOnly />
                  <Field label='Nombre del evento' value={form.eventName} readOnly />
                </>
              )}

              <Field label='Lugar *' name='place' value={form.place} onChange={updateField} required />
              <Field
                label='Fecha *'
                type='date'
                min={getMinimumRequestDate(requestType === 'new_requirement' ? 6 : 3)}
                name='requestDate'
                value={form.requestDate}
                onChange={updateField}
                hint={requestType === 'new_requirement'
                  ? 'Debe solicitarse con mínimo 6 días hábiles de anticipación.'
                  : 'Debe solicitarse con mínimo 3 días hábiles de anticipación.'}
                required
              />
              <Field label='Dirección *' name='address' value={form.address} onChange={updateField} required />
              {requestType === 'new_requirement' ? (
                <>
                  <label>
                    <span>Comuna *</span>
                    <select name='municipalityId' value={form.municipalityId} onChange={updateField} required>
                      <option value=''>Selecciona una comuna</option>
                      {(context.municipalities || []).map(item => (
                        <option value={item.id} key={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <Field label='# de entrega *' name='deliveryNumber' value={form.deliveryNumber} onChange={updateField} required />
                  <Field label='Cantidad *' type='number' min='1' name='quantity' value={form.quantity} onChange={updateField} required />
                </>
              )}
              <Field label='Hora *' type='time' name='requestTime' value={form.requestTime} onChange={updateField} required />
              <Field
                label='Teléfono *'
                type='tel'
                name='phone'
                value={form.phone}
                onChange={updateField}
                inputMode='numeric'
                pattern='3[0-9]{9}'
                minLength='10'
                maxLength='10'
                title='Debe tener 10 dígitos y comenzar por 3'
                required
              />
              <Field label='Responsable' value={context.currentUser?.fullName || ''} readOnly />
            </div>
            <div className='requirements-form__actions'>
              <button type='button' onClick={closeCreator}>Cancelar</button>
              <button type='submit' disabled={saving}>{saving ? 'Guardando...' : editingId ? 'Guardar y reenviar' : 'Enviar a Logística'}</button>
            </div>
          </form>
        )}

        <section className='requirements-page__list-section'>
          <SectionTitle step='HISTORIAL' title='Mis solicitudes' />
          {loading ? (
            <p className='requirements-page__empty'>Cargando solicitudes...</p>
          ) : requests.length === 0 ? (
            <div className='requirements-page__empty'>
              <span className='material-symbols-rounded'>inbox</span>
              Aún no tienes requerimientos registrados.
            </div>
          ) : (
            <div className='requirements-page__list'>
              {requests.map(item => (
                <article className='requirements-request' key={item.id}>
                  <div className='requirements-request__main'>
                    <span className='material-symbols-rounded'>{item.requestType === 'bag_request' ? 'package_2' : 'event_note'}</span>
                    <div>
                      <h3>{item.eventName || `Solicitud de bolsa #${item.deliveryNumber}`}</h3>
                      <p>{item.place} · {item.requestDate || item.requestTime}</p>
                    </div>
                  </div>
                  <dl>
                    <div><dt>Código</dt><dd>{item.code || 'Pendiente de asignación'}</dd></div>
                    <div><dt>Estado</dt><dd className={`request-status request-status--${item.status}`}>{STATUS_LABELS[item.status]}</dd></div>
                  </dl>
                  {item.reviewObservation && <p className='requirements-request__observation'><strong>Observación:</strong> {item.reviewObservation}</p>}
                  {(item.status === 'pending' || item.status === 'returned') ? (
                    <div className='requirements-request__actions'>
                      <button type='button' onClick={() => editRequest(item)}>
                        <span className='material-symbols-rounded'>edit</span>
                        Editar requerimiento
                      </button>
                    </div>
                  ) : item.status === 'accepted' ? (
                    <div className='requirements-request__actions'>
                      <button
                        type='button'
                        className='is-novelty'
                        onClick={() => editRequest(item, true)}>
                        <span className='material-symbols-rounded'>edit_note</span>
                        Novedad
                      </button>
                      {evidenceReadyIds.includes(item.id) ? (
                        <button type='button' onClick={() => openEvidenceDownload(item)}>
                          <span className='material-symbols-rounded'>download</span>
                          Descargar evidencias logísticas
                        </button>
                      ) : (
                        <button
                          type='button'
                          className='is-no-novelty'
                          onClick={() => setEvidenceReadyIds(current => (
                            current.includes(item.id) ? current : [...current, item.id]
                          ))}>
                          <span className='material-symbols-rounded'>check_circle</span>
                          Sin novedad
                        </button>
                      )}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {evidenceRequest && (
        <div className='logistics-evidence-modal' role='presentation' onMouseDown={event => {
          if (event.target === event.currentTarget) closeEvidenceDownload();
        }}>
          <form className='logistics-evidence-modal__panel' onSubmit={downloadEvidence} role='dialog' aria-modal='true' aria-labelledby='logistics-evidence-title'>
            <header>
              <div>
                <span>EVIDENCIAS LOGÍSTICAS</span>
                <h2 id='logistics-evidence-title'>Localizar listados de asistencia</h2>
                <p>Esta selección solo se utilizará para preparar la descarga y no se guardará.</p>
              </div>
              <button type='button' onClick={closeEvidenceDownload} aria-label='Cerrar'>×</button>
            </header>

            {evidenceLoading ? (
              <div className='logistics-evidence-modal__loading'>Cargando opciones...</div>
            ) : evidenceContext?.hasChecklist === false ? (
              <div className='logistics-evidence-modal__warning' role='alert'>
                <span className='material-symbols-rounded'>warning</span>
                <div>
                  <strong>Lista de chequeo pendiente</strong>
                  <p>Logística aún no ha cargado la lista de chequeo de este requerimiento. No es posible descargar las evidencias hasta que el archivo esté disponible.</p>
                </div>
              </div>
            ) : evidenceContext && (
              <div className='logistics-evidence-modal__fields'>
                <div className='logistics-evidence-modal__select logistics-evidence-modal__dropdown'>
                  <span>Nombre de los mediadores *</span>
                  <CustomFilterSelect
                    optionsList={evidenceContext.mediators}
                    placeholder='Selecciona uno o varios mediadores'
                    valueEdit={evidenceMediatorLabel}
                    keyName='mediatorIds'
                    addInfo={selectEvidenceMediators}
                    searchable
                    searchPlaceholder='Buscar mediador...'
                    multiple
                    selectedValues={evidenceSelection.mediatorIds}
                    dropdownClassName='logistics-evidence-dropdown'
                  />
                </div>

                <div className='logistics-evidence-modal__select logistics-evidence-modal__dropdown'>
                  <span>Institución *</span>
                  <CustomFilterSelect
                    optionsList={evidenceSchools.map(school => ({
                      ...school,
                      name: `${school.name}${school.code ? ` · ${school.code}` : ''}`
                    }))}
                    placeholder='Selecciona una o varias instituciones'
                    valueEdit={evidenceSchoolLabel}
                    keyName='schoolIds'
                    addInfo={selectEvidenceSchools}
                    searchable
                    searchPlaceholder='Buscar institución...'
                    disabled={evidenceSelection.mediatorIds.length === 0}
                    multiple
                    selectedValues={evidenceSelection.schoolIds}
                    dropdownClassName='logistics-evidence-dropdown'
                  />
                </div>

                <fieldset disabled={evidenceSelection.schoolIds.length === 0}>
                  <legend>Grupos *</legend>
                  <div className='logistics-evidence-modal__options'>
                    {evidenceGroups.length === 0 ? (
                      <p>Selecciona primero los mediadores y la institución.</p>
                    ) : evidenceGroups.map(group => (
                      <label key={group.id}>
                        <input
                          type='checkbox'
                          checked={evidenceSelection.groupIds.includes(Number(group.id))}
                          onChange={() => toggleEvidenceGroup(Number(group.id))}
                        />
                        <span>{group.code || `Grupo ${group.id}`}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset disabled={evidenceSelection.groupIds.length === 0}>
                  <legend>Sesiones donde se consumió el requerimiento *</legend>
                  <div className='logistics-evidence-modal__session-groups'>
                    {evidenceSessionGroups.length === 0 ? (
                      <p>Selecciona al menos un grupo.</p>
                    ) : evidenceSessionGroups.map(group => (
                      <section className='logistics-evidence-modal__session-group' key={group.id}>
                        <div className='logistics-evidence-modal__session-group-title'>
                          <span className='material-symbols-rounded'>groups</span>
                          <div>
                            <h4>{group.code}</h4>
                            {group.schoolName && <p>{group.schoolName}</p>}
                          </div>
                        </div>
                        <div className='logistics-evidence-modal__options logistics-evidence-modal__options--sessions'>
                          {group.sessions.map(session => (
                            <label className={!session.hasAttendanceEvidence ? 'has-missing-evidence' : ''} key={session.id}>
                              <input
                                type='checkbox'
                                checked={evidenceSelection.meetingIds.includes(Number(session.id))}
                                onChange={() => toggleEvidenceSession(Number(session.id))}
                              />
                              <span>
                                {session.sourceType === 'incidence_action'
                                  ? 'Acción de incidencia'
                                  : `Sesión ${session.order || '—'}`}
                                {session.meetingDate ? ` · ${String(session.meetingDate).slice(0, 10)}` : ''}
                              </span>
                              <small>{session.hasAttendanceEvidence ? 'Asistencia disponible' : 'Sin evidencia de asistencia'}</small>
                            </label>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </fieldset>
              </div>
            )}

            <footer>
              <button type='button' onClick={closeEvidenceDownload} disabled={evidenceDownloading}>Cancelar</button>
              <button type='submit' disabled={evidenceLoading || evidenceDownloading || !evidenceContext?.hasChecklist}>
                <span className='material-symbols-rounded'>download</span>
                {evidenceDownloading ? 'Preparando...' : 'Descargar evidencias'}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}

function SectionTitle({ step, title, id, onClose }) {
  return (
    <div className='requirements-page__section-title'>
      <div><span>{step}</span><h2 id={id}>{title}</h2></div>
      {onClose && <button type='button' onClick={onClose} aria-label='Cerrar'>×</button>}
    </div>
  );
}

function Field({ label, textarea, wide, hint, ...props }) {
  return (
    <label className={wide ? 'is-wide' : ''}>
      <span>{label}</span>
      {textarea ? <textarea {...props} /> : <input {...props} />}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export default Requirements;
